import React from 'react';
import { Link } from 'react-router-dom';
import { Restaurant } from '../../types';
import { useAuth } from '../../context/AuthContext';
import * as S from './RestaurantHeaderStyles';

export interface RestaurantHeaderProps {
  restaurant: Restaurant | null;
  logoRef: React.RefObject<HTMLDivElement>;
  isRestaurantOpen: boolean;
  nextOpeningTime: string;
  closingTime: string;
  onInfoClick: () => void;
}

export const RestaurantCover: React.FC<{
  restaurant: Restaurant | null;
  logoRef: React.RefObject<HTMLDivElement>;
}> = ({ restaurant, logoRef }) => {
  return (
    <S.CoverContainer>
      <S.CoverImage 
        backgroundImage={restaurant?.coverImage || 'https://via.placeholder.com/1200x400'} 
      />
      {restaurant && (
        <S.LogoImage 
          ref={logoRef}
          logo={restaurant.logo || 'https://via.placeholder.com/150x150'}
        />
      )}
    </S.CoverContainer>
  );
};

export const RestaurantInfo: React.FC<{
  restaurant: Restaurant;
  isRestaurantOpen: boolean;
  nextOpeningTime: string;
  closingTime: string;
  onInfoClick: () => void;
}> = ({
  restaurant,
  isRestaurantOpen,
  nextOpeningTime,
  closingTime,
  onInfoClick
}) => {
  return (
    <S.InfoContainer className="restaurant-info-container">
      <div className="d-flex flex-column align-items-center align-items-md-start">
        <h1 className="fw-bolder h3 text-dark">{restaurant.name}</h1>
        <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-1 flex-wrap">
          {restaurant.restaurantCategories?.map((c, index) => (
            <span key={c.id} className="badge bg-light text-muted fw-normal px-2 pb-1 rounded-pill border small">
              {c.name}
            </span>
          ))}
          {restaurant.tags?.map(tag => (
            <span key={tag.id} className="badge bg-light text-muted fw-normal px-2 pb-1 rounded-pill border small">
              <i className="fas fa-tag me-1" />
              {tag.name}
            </span>
          ))}
        </div>
        {(restaurant.deliveryInfo || restaurant.deliveryOptions) && (
          <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-2 flex-wrap mt-2">
            {restaurant.deliveryInfo && (
              <span className="badge rounded-pill text-dark bg-warning bg-opacity-10 border border-warning-subtle fw-normal px-2 pb-1 small">
                Entrega: {restaurant.deliveryInfo.minTime}-{restaurant.deliveryInfo.maxTime} min
              </span>
            )}
          </div>
        )}
      </div>

      <S.BadgesWrapper>
        {/* Status de abertura */}
        <S.StatusBadge isOpen={isRestaurantOpen}>
          <span className="status-indicator" />
          <span className="status-text">
            {isRestaurantOpen ? `Aberto até às ${closingTime}` : 'Fechado no momento'}
          </span>
          {!isRestaurantOpen && (
            <span className="text-muted small border-start ps-2">abre {nextOpeningTime}</span>
          )}
        </S.StatusBadge>

        {/* Avaliação */}
        {restaurant.rating !== undefined && restaurant.rating > 0 && (
          <S.InfoBadge>
            <i className="fas fa-star text-warning"></i>
            <span className="fw-bold text-dark">{restaurant.rating.toFixed(1)}</span>
            {restaurant.reviewCount !== undefined && restaurant.reviewCount > 0 && (
              <span className="text-muted small border-start ps-2">{restaurant.reviewCount} avaliações</span>
            )}
          </S.InfoBadge>
        )}

        {restaurant.deliveryOptions?.minimumOrderValue !== undefined && (
          <S.InfoBadge>
            <i className="fas fa-shopping-basket text-dark"></i>
            <span className="fw-medium text-dark">
              Pedido mín.: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(restaurant.deliveryOptions.minimumOrderValue)}
            </span>
          </S.InfoBadge>
        )}

        {/* Endereço */}
        <S.InfoBadge>
          <i className="fas fa-map-marker-alt text-danger"></i>
          <span className="fw-medium text-dark">
            {restaurant.address?.street && `${restaurant.address.street}, `}
            {restaurant.address?.city} - {restaurant.address?.state}
          </span>
        </S.InfoBadge>
        
        {/* Link de Mais Informações */}
        <S.InfoButton 
          onClick={onInfoClick}
          className="btn"
          primaryColor={restaurant.theme?.primary}
        >
          <span>Ver mais</span>
          <i className="fas fa-chevron-right small opacity-75"></i>
        </S.InfoButton>
      </S.BadgesWrapper>
    </S.InfoContainer>
  );
};

export const CashbackCard: React.FC<{
  restaurant: Restaurant;
}> = ({ restaurant }) => {
  const { isAuthenticated } = useAuth();
  
  if (!restaurant.cashbackConfig?.enabled) return null;

  return (
    <S.CashbackBadge primaryColor={restaurant.theme?.primary} className="w-100 mt-3">
      <div className="cashback-header">
        <div className="cashback-icon">
          <i className="fas fa-coins"></i>
        </div>
        <span className="cashback-title">Sistema de Cashback</span>
      </div>
      
      <div className="cashback-content">
        <span className="cashback-text">
          Receba <strong>{restaurant.cashbackConfig.type === 'PERCENTAGE' ? `${restaurant.cashbackConfig.value}%` : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(restaurant.cashbackConfig.value)}</strong> de volta em todas as suas compras.
        </span>
      </div>

      <Link to="/cashback" className="cashback-link">
        {isAuthenticated ? 'Ver meu saldo' : 'Saiba mais'}
      </Link>
    </S.CashbackBadge>
  );
};

const RestaurantHeader: React.FC<RestaurantHeaderProps> = ({
  restaurant,
  logoRef,
  isRestaurantOpen,
  nextOpeningTime,
  closingTime,
  onInfoClick
}) => {
  return (
    <>
      <RestaurantCover restaurant={restaurant} logoRef={logoRef} />

      {restaurant && (
        <div className="row mt-3 mb-1 g-3">
          <div className="col-12 col-md-8 col-lg-9 mt-0">
            <RestaurantInfo 
              restaurant={restaurant}
              isRestaurantOpen={isRestaurantOpen}
              nextOpeningTime={nextOpeningTime}
              closingTime={closingTime}
              onInfoClick={onInfoClick}
            />
          </div>

          <div className="col-12 col-md-4 col-lg-3 d-flex">
            <CashbackCard restaurant={restaurant} />
          </div>
        </div>
      )}
    </>
  );
};

export default RestaurantHeader;
